<template>
  <div class="delivery-address-selector">
    <!-- Need Delivery Toggle -->
    <v-switch
      v-if="showToggle"
      v-model="needDelivery"
      :label="t.delivery?.needDelivery || 'Need delivery'"
      color="primary"
      density="compact"
      hide-details
      class="mb-3"
    ></v-switch>

    <div v-if="needDelivery || !showToggle">
      <!-- Delivery Type Selection -->
      <v-select
        v-model="selectedDeliveryType"
        :items="deliveryTypes"
        :item-title="formatDeliveryType"
        item-value="id"
        return-object
        :label="t.delivery?.selectMethod || 'Delivery method'"
        variant="outlined"
        density="compact"
        class="mb-3"
      ></v-select>

      <!-- Saved Addresses Dropdown -->
      <div v-if="savedAddresses.length > 0" class="mb-3">
        <v-select
          v-model="selectedAddressId"
          :items="savedAddressOptions"
          item-title="display_name"
          item-value="id"
          :label="t.delivery?.selectSavedAddress || 'Select saved address'"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="onAddressSelected"
        >
          <template v-slot:append-inner>
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              @click.stop="$emit('manageAddresses')"
              :title="t.delivery?.manageAddresses || 'Manage addresses'"
            >
              <v-icon size="small">mdi-cog</v-icon>
            </v-btn>
          </template>
        </v-select>
      </div>

      <div v-else-if="clientId" class="mb-3 text-caption text-grey">
        {{ t.delivery?.noSavedAddresses || 'No saved addresses' }}
        <v-btn
          variant="text"
          size="x-small"
          color="primary"
          @click="$emit('manageAddresses')"
        >
          {{ t.addresses?.add || 'Add' }}
        </v-btn>
      </div>

      <!-- Address Form Fields (if courier type selected) -->
      <div v-if="needsDeliveryAddress" class="address-form">
        <div class="text-subtitle-2 mb-2">{{ t.delivery?.address || 'Delivery Address' }}</div>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model="addressData.contact_name"
              :label="t.delivery?.contactName || 'Contact Name'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="addressData.contact_phone"
              :label="t.delivery?.contactPhone || 'Phone'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="8">
            <v-text-field
              v-model="addressData.street"
              :label="t.delivery?.street || 'Street'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="addressData.street_number"
              :label="t.delivery?.streetNumber || 'Number'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="3">
            <v-text-field
              v-model="addressData.building"
              :label="t.delivery?.building || 'Bloc'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="addressData.entrance"
              :label="t.delivery?.entrance || 'Scara'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="addressData.floor"
              :label="t.delivery?.floor || 'Etaj'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="addressData.apartment"
              :label="t.delivery?.apartment || 'Ap'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="4">
            <v-text-field
              v-model="addressData.city"
              :label="t.delivery?.city || 'City'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="addressData.county"
              :label="t.delivery?.county || 'County'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field
              v-model="addressData.postal_code"
              :label="t.delivery?.postalCode || 'Postal Code'"
              variant="outlined"
              density="compact"
              @input="onAddressModified"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-text-field
          v-model="addressData.other_details"
          :label="t.delivery?.otherDetails || 'Other details for courier'"
          variant="outlined"
          density="compact"
          @input="onAddressModified"
        ></v-text-field>

        <!-- Save Error Alert -->
        <v-alert
          v-if="saveError"
          type="error"
          density="compact"
          class="mt-2"
          closable
          @click:close="saveError = null"
        >
          {{ saveError }}
        </v-alert>

        <!-- Update Address Prompt -->
        <v-alert
          v-if="showUpdatePrompt && selectedAddressId"
          type="info"
          density="compact"
          class="mt-2"
        >
          <div class="d-flex justify-space-between align-center flex-wrap">
            <span class="text-body-2">{{ t.delivery?.addressModified || 'Address modified. Save changes?' }}</span>
            <div class="mt-1">
              <v-btn size="small" variant="text" color="primary" @click="updateExistingAddress" class="mr-1" :loading="saving">
                {{ t.delivery?.updateAddress || 'Update' }}
              </v-btn>
              <v-btn size="small" variant="text" @click="saveAsNewAddress" :loading="saving">
                {{ t.delivery?.saveAsNew || 'Save as New' }}
              </v-btn>
              <v-btn size="small" variant="text" @click="dismissUpdatePrompt" :disabled="saving">
                {{ t.common?.no || 'No' }}
              </v-btn>
            </div>
          </div>
        </v-alert>
      </div>

      <!-- Delivery Cost Display -->
      <div v-if="selectedDeliveryType" class="mt-3">
        <v-card variant="tonal" class="pa-2">
          <div class="d-flex justify-space-between">
            <span>{{ t.delivery?.cost || 'Delivery cost' }}:</span>
            <span class="font-weight-bold">{{ deliveryCost.toFixed(2) }} {{ selectedDeliveryType.currency_code || 'EUR' }}</span>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getClientDeliveryAddresses, saveClientDeliveryAddress } from "@/composables/useClientDeliveryAddressAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'DeliveryAddressSelector',
  props: {
    showToggle: {
      type: Boolean,
      default: true
    },
    deliveryTypes: {
      type: Array,
      default: () => []
    },
    clientId: {
      type: [Number, String],
      default: null
    },
    modelValue: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'deliveryChanged', 'manageAddresses'],
  data() {
    return {
      needDelivery: false,
      selectedDeliveryType: null,
      selectedAddressId: null,
      savedAddresses: [],
      loadingAddresses: false,
      addressData: this.getEmptyAddressData(),
      originalAddressData: null,
      showUpdatePrompt: false,
      addressModified: false,
      saveError: null,
      saving: false
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    token() {
      return store.getters.getToken;
    },
    savedAddressOptions() {
      return [
        { id: null, display_name: '-- ' + (this.t.delivery?.manualEntry || 'Enter manually') + ' --' },
        ...this.savedAddresses.map(addr => ({
          ...addr,
          display_name: `${addr.location_name} - ${addr.city}`
        }))
      ];
    },
    needsDeliveryAddress() {
      if (!this.selectedDeliveryType) return false;
      const name = (this.selectedDeliveryType.name || '').toLowerCase();
      return name.includes('courier') || name.includes('curier') || name.includes('delivery') || name.includes('livrare');
    },
    deliveryCost() {
      return parseFloat(this.selectedDeliveryType?.cost || 0);
    },
    isValid() {
      if (!this.needDelivery && this.showToggle) return true;
      if (!this.selectedDeliveryType) return false;
      if (this.needsDeliveryAddress) {
        return !!(this.addressData.contact_name && this.addressData.street &&
                  this.addressData.city && this.addressData.contact_phone);
      }
      return true;
    }
  },
  methods: {
    getEmptyAddressData() {
      return {
        location_name: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        street: '',
        street_number: '',
        building: '',
        entrance: '',
        floor: '',
        apartment: '',
        intercom: '',
        sector: '',
        postal_code: '',
        city: '',
        county: '',
        country: 'Romania',
        other_details: ''
      };
    },

    formatDeliveryType(item) {
      if (!item) return '';
      const cost = item.cost || 0;
      const currency = item.currency_code || 'EUR';
      return cost > 0 ? `${item.name} - ${cost} ${currency}` : `${item.name} (Free)`;
    },

    async loadSavedAddresses() {
      if (!this.clientId || !this.token) return;

      this.loadingAddresses = true;
      try {
        const response = await getClientDeliveryAddresses(this.token, this.clientId);
        if (!response.error) {
          this.savedAddresses = response.addresses || [];
          log("Loaded saved addresses for selector:");
          log(this.savedAddresses);

          // Auto-select the first saved address if available
          if (this.savedAddresses.length > 0 && !this.selectedAddressId) {
            const firstAddress = this.savedAddresses[0];
            this.selectedAddressId = firstAddress.id;
            this.addressData = { ...firstAddress };
            this.originalAddressData = { ...firstAddress };
            this.emitValue();
          }
        }
      } catch (err) {
        log("Error loading saved addresses:");
        log(err);
      } finally {
        this.loadingAddresses = false;
      }
    },

    onAddressSelected(addressId) {
      log("Address selected: " + addressId);
      this.showUpdatePrompt = false;
      this.addressModified = false;

      if (addressId) {
        const address = this.savedAddresses.find(a => a.id === addressId);
        if (address) {
          this.addressData = { ...address };
          this.originalAddressData = { ...address };
        }
      } else {
        this.addressData = this.getEmptyAddressData();
        this.originalAddressData = null;
      }

      this.emitValue();
    },

    onAddressModified() {
      // Clear any previous save error when user modifies
      this.saveError = null;

      if (this.selectedAddressId && this.originalAddressData) {
        // Check if data has changed
        const hasChanges = Object.keys(this.originalAddressData).some(key => {
          if (['id', 'created_at', 'updated_at', 'status', 'full_address', 'display_name'].includes(key)) return false;
          return this.addressData[key] !== this.originalAddressData[key];
        });

        if (hasChanges && !this.addressModified) {
          this.addressModified = true;
          this.showUpdatePrompt = true;
        }
      }

      this.emitValue();
    },

    async updateExistingAddress() {
      this.saveError = null;

      if (!this.selectedAddressId || !this.clientId) {
        this.saveError = this.t.delivery?.selectClientFirst || "Selecteaza un client inainte de a salva adresa";
        log("Cannot update address: missing selectedAddressId or clientId");
        return;
      }

      this.saving = true;
      try {
        const data = {
          ...this.addressData,
          id: this.selectedAddressId,
          client_id: this.clientId
        };

        const response = await saveClientDeliveryAddress(this.token, data);
        if (!response.error) {
          this.originalAddressData = { ...this.addressData };
          this.showUpdatePrompt = false;
          this.addressModified = false;
          // Reload addresses
          await this.loadSavedAddresses();
        } else {
          this.saveError = response.message || "Eroare la salvarea adresei";
        }
      } catch (err) {
        log("Error updating address:");
        log(err);
        this.saveError = "Eroare la salvarea adresei";
      } finally {
        this.saving = false;
      }
    },

    async saveAsNewAddress() {
      this.saveError = null;

      if (!this.clientId) {
        this.saveError = this.t.delivery?.selectClientFirst || "Selecteaza un client inainte de a salva adresa";
        log("Cannot save new address: missing clientId");
        return;
      }

      if (!this.addressData.location_name) {
        this.addressData.location_name = 'New Address';
      }

      this.saving = true;
      try {
        const data = {
          ...this.addressData,
          id: null, // Force create new
          client_id: this.clientId
        };

        const response = await saveClientDeliveryAddress(this.token, data);
        if (!response.error) {
          this.showUpdatePrompt = false;
          this.addressModified = false;
          // Reload and select new address
          await this.loadSavedAddresses();
          if (response.address?.id) {
            this.selectedAddressId = response.address.id;
            this.originalAddressData = { ...this.addressData };
          }
        } else {
          this.saveError = response.message || "Eroare la salvarea adresei";
        }
      } catch (err) {
        log("Error saving new address:");
        log(err);
        this.saveError = "Eroare la salvarea adresei";
      } finally {
        this.saving = false;
      }
    },

    dismissUpdatePrompt() {
      this.showUpdatePrompt = false;
      this.saveError = null;
    },

    emitValue() {
      const value = {
        needDelivery: this.needDelivery,
        deliveryTypeId: this.selectedDeliveryType?.id,
        deliveryTypeName: this.selectedDeliveryType?.name,
        selectedAddressId: this.selectedAddressId,
        deliveryDetails: this.needsDeliveryAddress ? this.addressData : null,
        deliveryCost: this.deliveryCost,
        deliveryCurrency: this.selectedDeliveryType?.currency_code || 'EUR',
        isValid: this.isValid
      };
      this.$emit('update:modelValue', value);
      this.$emit('deliveryChanged', value);
    }
  },
  watch: {
    needDelivery() {
      this.emitValue();
    },
    selectedDeliveryType() {
      this.emitValue();
    },
    deliveryTypes: {
      immediate: true,
      handler(types) {
        if (types && types.length > 0 && !this.selectedDeliveryType) {
          this.selectedDeliveryType = types[0];
        }
      }
    },
    clientId: {
      immediate: true,
      handler(newClientId, oldClientId) {
        if (newClientId) {
          // Reset selection when client changes
          if (oldClientId && newClientId !== oldClientId) {
            this.selectedAddressId = null;
            this.addressData = this.getEmptyAddressData();
            this.originalAddressData = null;
            this.showUpdatePrompt = false;
            this.addressModified = false;
          }
          this.loadSavedAddresses();
        } else {
          // Clear addresses when no client
          this.savedAddresses = [];
          this.selectedAddressId = null;
          this.addressData = this.getEmptyAddressData();
        }
      }
    }
  },
  mounted() {
    if (this.deliveryTypes.length > 0 && !this.selectedDeliveryType) {
      this.selectedDeliveryType = this.deliveryTypes[0];
    }
    if (this.clientId) {
      this.loadSavedAddresses();
    }
  }
});
</script>

<style scoped>
.delivery-address-selector {
  width: 100%;
}
.address-form {
  background: rgba(0, 0, 0, 0.02);
  padding: 12px;
  border-radius: 8px;
}
</style>
